import {useState, useEffect} from "react";
import {useParams} from "react-router-dom";
import baseUrl from "../../baseUrl";
import {requester} from "../../services/requester";
import {downloadPdf} from "../../services/downloadPdf";

// Обща основа за пълната справка по ползвател (Г3):
//   variant="full" (С3) — с замени;
//   variant="general" (С4) — без замени;
//   variant="correction" (С8) — с замени + колона „Корекция".
// И трите ползват GET /reports/s5; variant сменя само PDF генератора.
export const UserFullReport = ({title, variant = "full", showDeals = true, showCorrection = false}) => {
    const {year, settlement_ekatte, bullstat} = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const request = requester();

    useEffect(() => {
        request.get(`${baseUrl()}/reports/s5/${year}/${settlement_ekatte}/${bullstat}`)
            .then(res => {
                setData(res);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [year, settlement_ekatte, bullstat]);

    const handlePdf = () => {
        const q = variant === "full" ? "" : `&variant=${variant}`;
        const prefix = variant === "general" ? "S4" : variant === "correction" ? "S8" : "S3";
        downloadPdf(`/reports/s5/${year}/${settlement_ekatte}/${bullstat}?format=pdf${q}`,
            `${prefix}_${bullstat}_${settlement_ekatte}_${year}.pdf`);
    };

    if (loading) return <div className="mt-3">Зареждане...</div>;
    if (!data) return <div className="mt-3 alert alert-warning">Няма данни.</div>;

    return (
        <div className="mt-3">
            <div className="d-flex justify-content-between align-items-start mb-2">
                <h5 style={{color: "var(--app-gold)"}}>{title}</h5>
            </div>
            <UserFullReportBody data={data} showDeals={showDeals} showCorrection={showCorrection}
                                onPdf={handlePdf}/>
        </div>
    );
};

// Ъ16: тялото на справката за ЕДНО землище — шапка, картички, таблици. Ползва
// се от единичната справка и от справката за цяла община (по веднъж на землище).
// `onPdf` — бутонът „PDF" под картичките; при общината той е един, най-горе.
export const UserFullReportBody = ({data, showDeals = true, showCorrection = false, onPdf}) => {
    const c = data.company;
    const s = data.settlement;
    const d = data.data;

    return (
        <>
            <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                    <div className="text-muted">{data.date} | ЕКАТТЕ: {s.ekatte} | {s.settlement}, община {s.municipality}</div>
                    <div className="text-muted">Стопанска {data.year} година</div>
                    <div className="fw-bold mt-4">{c.company_name}, Булстат: {c.bullstat}</div>
                </div>
                <div className="text-end">
                    <div className="card px-3 py-1 mb-1 text-center"
                         style={{borderColor: "var(--app-gold)", backgroundColor: "var(--app-surface-3)"}}>
                        <span className="fw-bold" style={{fontSize: "1.2em", color: "var(--app-gold)"}}>
                            Правно: {d.pravno.toFixed(2)}
                        </span>
                    </div>
                    <div className="card px-3 py-1 text-center"
                         style={{borderColor: "var(--app-gold)", backgroundColor: "var(--app-surface-3)"}}>
                        <span className="fw-bold" style={{fontSize: "1.2em"}}>
                            Изчертано: {d.drawings_sum.toFixed(2)}
                        </span>
                    </div>
                    {onPdf && <button className="btn btn-outline-danger btn-sm mt-1" onClick={onPdf}>PDF</button>}
                </div>
            </div>

            <table className="table table-sm table-bordered mb-3" style={{maxWidth: "700px"}}>
                <thead className="tableHead">
                <tr>
                    <th className="text-end">Деклар.</th>
                    <th className="text-end">Негодна</th>
                    <th className="text-end">Годна</th>
                    <th className="text-end">БП</th>
                    <th className="text-end">Пътища</th>
                    <th className="text-end">ДЗ</th>
                    {showCorrection && <th className="text-end">Корекция</th>}
                </tr>
                </thead>
                <tbody>
                <tr>
                    <td className="text-end">{d.declared_land.toFixed(2)}</td>
                    <td className="text-end">{d.unsuitable_land.toFixed(2)}</td>
                    <td className="text-end">{d.suitable_land.toFixed(2)}</td>
                    <td className="text-end">{d.white_spots.toFixed(2)}</td>
                    <td className="text-end">{d.roads.toFixed(2)}</td>
                    <td className="text-end">{d.deals_sum.toFixed(2)}</td>
                    {showCorrection && <td className="text-end">{d.correction.toFixed(2)}</td>}
                </tr>
                </tbody>
            </table>

            {showDeals && data.deals.length > 0 && (
                <>
                    <h6 className="fw-bold">Замени (ДЗ)</h6>
                    <table className="table table-sm table-bordered">
                        <thead className="tableHead">
                        <tr>
                            <th>Ползвател</th>
                            <th>За село</th>
                            <th className="text-end">Сума (дка)</th>
                            <th>Посока</th>
                        </tr>
                        </thead>
                        <tbody>
                        {data.deals.map((deal, i) => (
                            <tr key={i}>
                                <td>{deal.company_other}</td>
                                <td>{deal.settlement_other}</td>
                                <td className="text-end">
                                    {Math.abs(deal.amount).toFixed(2)}
                                    {deal.count > 1 && <span className="text-muted"> ({deal.count})</span>}
                                </td>
                                <td>{deal.bullstat_receiving === c.bullstat ? "получава" : "дава"}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </>
            )}

            {data.drawings.length > 0 && (
                <>
                    <h6 className="fw-bold mt-3">Изчертани площи</h6>
                    <table className="table table-sm table-bordered">
                        <thead className="tableHead">
                        <tr>
                            <th>№</th>
                            <th>Масив</th>
                            <th>МПО</th>
                            <th className="text-end">Площ (дка)</th>
                            <th>Култура</th>
                            {/* Ш3: забележката към реда — след „Култура". */}
                            <th>Забележка</th>
                        </tr>
                        </thead>
                        <tbody>
                        {data.drawings.map((dr, i) => (
                            <tr key={i}>
                                <td>{i + 1}</td>
                                <td>{dr.massif_number}</td>
                                <td>{dr.mpo_number}</td>
                                <td className="text-end">{dr.area.toFixed(2)}</td>
                                <td>{dr.crop}</td>
                                <td>{dr.note}</td>
                            </tr>
                        ))}
                        </tbody>
                        <tfoot>
                        <tr className="fw-bold">
                            <td colSpan={3}>ОБЩО</td>
                            <td className="text-end">{d.drawings_sum.toFixed(2)}</td>
                            <td></td>
                            <td></td>
                        </tr>
                        </tfoot>
                    </table>
                </>
            )}
        </>
    );
};
